import { useEffect, useMemo, useRef, useState, type JSX, type ReactNode } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import gsap from 'gsap';
import type { TimerPreset } from '@susdle/shared';
import { PageTurnLeaf } from '../shared/components/PageTurnLeaf.js';
import { prefersReducedMotion } from '../preferences/reducedMotion.js';
import { Spread } from '../shared/components/Spread.js';
import { AvatarFrame, EmptyChair } from '../shared/components/AvatarFrame.js';
import { Wordmark } from '../lobby/components/Wordmark.js';
import { VillageIllustration } from '../lobby/components/VillageIllustration.js';
import { PrintedCast } from '../lobby/components/PrintedCast.js';
import { HostControls } from '../lobby/components/HostControls.js';
import { SignatureBand } from '../lobby/components/SignatureBand.js';
import type { LobbyPlayer, LobbyView } from '../lobby/types.js';
import './MeetingTransition.css';

interface MeetingTransitionProps {
  /** Destination palette: where the meeting lands. */
  to: 'village' | 'ghost';
  /**
   * Snapshot of the lobby at the moment Begin fired. Used to render the
   * lobby's content on the snapshot's verso and the leaf's front face so
   * the page that's being turned literally carries its content into the
   * apex (rather than the prior behavior of fading the content first and
   * then flipping an empty page).
   *
   * Required: a transition without a snapshot would render a blank Spread,
   * which is the bug this rewrite is fixing.
   */
  lobbyView: LobbyView;
  /** Fires after the whole cinematic settles. The parent unmounts this
   *  overlay in response, which exposes the meeting underneath. */
  onComplete: () => void;
}

const MIN_PLAYERS = 3;
const MOBILE_BREAKPOINT_QUERY = '(max-width: 880px)';

/**
 * The Lobby → GhostMeeting cinematic.
 *
 * Desktop (>880px): a chapbook page-turn. The leaf's front face carries the
 * lobby's recto content (Wordmark + VillageIllustration + PrintedCast);
 * the snapshot's verso carries the lobby's verso content
 * (Company grid + HostControls + SignatureBand). As the leaf rotates 0° →
 * -180°, the recto content tilts, foreshortens, and slips out of view past
 * -90° via backface-visibility — the page that's being turned literally
 * carries its content. The back face shows the destination palette so the
 * chromatic-divergence beat lands as the page settles. Snapshot fades
 * partway through the second half so the meeting underneath is what's
 * exposed when the leaf clears.
 *
 * Mobile (≤880px): the spread collapses to a single column and the page-turn
 * metaphor doesn't survive that layout. Falls back to a palette-shift
 * crossfade — the lobby snapshot desaturates and fades over ~900ms,
 * revealing the meeting (already painted in the destination palette)
 * underneath. The chromatic divergence still lands as a felt event for
 * Ghost POV (cool palette emerging through the fade); on V/F POV the
 * destination is warm so the fade just reveals the warm meeting.
 *
 * Reduced motion: PageTurnLeaf bails immediately on desktop; the mobile
 * crossfade also collapses to instant. onComplete fires next tick.
 */
export function MeetingTransition({
  to,
  lobbyView,
  onComplete,
}: MeetingTransitionProps): JSX.Element {
  /* Pick the path once at mount and stick with it — a viewport resize
     mid-cinematic shouldn't swap the transition style under the user. */
  const [isMobile] = useState(
    () => window.matchMedia(MOBILE_BREAKPOINT_QUERY).matches,
  );

  const lobbyVerso = useMemo(
    () => <LobbyVersoSnapshot view={lobbyView} />,
    [lobbyView],
  );
  const lobbyRecto = useMemo(
    () => <LobbyRectoSnapshot view={lobbyView} />,
    [lobbyView],
  );

  if (isMobile) {
    return (
      <MobileCrossfade
        lobbyVerso={lobbyVerso}
        lobbyRecto={lobbyRecto}
        onComplete={onComplete}
      />
    );
  }

  return (
    <DesktopPageTurn
      to={to}
      lobbyVerso={lobbyVerso}
      lobbyRecto={lobbyRecto}
      onComplete={onComplete}
    />
  );
}

/* -------------------------------------------------------------------------- */
/* Desktop path: chapbook page-turn                                            */
/* -------------------------------------------------------------------------- */

interface DesktopPageTurnProps {
  to: 'village' | 'ghost';
  lobbyVerso: ReactNode;
  lobbyRecto: ReactNode;
  onComplete: () => void;
}

function DesktopPageTurn({
  to,
  lobbyVerso,
  lobbyRecto,
  onComplete,
}: DesktopPageTurnProps): JSX.Element {
  const snapshotRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const reducedMotion = prefersReducedMotion();
    if (reducedMotion || !snapshotRef.current) return;
    /* Fade the snapshot during the second half of the leaf rotation. By the
       time the leaf settles at -180° the meeting underneath is fully
       visible, so the leaf's cool back face hands off to the cool meeting
       without a warm intermediate. Timing scaled for the new 1200ms
       rotation: starts at 0.85s (~70% of the way through, just past apex),
       fades over 0.4s, fully gone before the leaf's own 180ms fade kicks in. */
    const tween = gsap.to(snapshotRef.current, {
      opacity: 0,
      duration: 0.4,
      delay: 0.85,
      ease: 'power2.in',
    });
    return () => { tween.kill(); };
  }, []);

  return (
    <div className="susdle-meeting-transition" aria-hidden>
      <div className="susdle-meeting-transition__snapshot" ref={snapshotRef}>
        {/* Snapshot's verso carries the lobby's actual verso content;
            the recto stays as paper because the leaf's front face is
            rendering the recto content on top of it. */}
        <Spread pov="village" verso={lobbyVerso} recto={null} />
      </div>
      <div className="susdle-meeting-transition__leaf-host">
        <div className="susdle-meeting-transition__leaf-frame">
          <PageTurnLeaf
            from="village"
            to={to}
            frontContent={lobbyRecto}
            onComplete={onComplete}
          />
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Mobile path: palette-shift crossfade                                        */
/* -------------------------------------------------------------------------- */

interface MobileCrossfadeProps {
  lobbyVerso: ReactNode;
  lobbyRecto: ReactNode;
  onComplete: () => void;
}

function MobileCrossfade({
  lobbyVerso,
  lobbyRecto,
  onComplete,
}: MobileCrossfadeProps): JSX.Element {
  const snapshotRef = useRef<HTMLDivElement>(null);
  const hasFiredRef = useRef(false);

  useEffect(() => {
    const reducedMotion = prefersReducedMotion();
    if (reducedMotion || !snapshotRef.current) {
      if (!hasFiredRef.current) {
        hasFiredRef.current = true;
        onComplete();
      }
      return;
    }

    /* Crossfade timeline:
         - 0–250ms: lobby content desaturates toward gray (filter: saturate
           0.2). The warm village reads as draining.
         - 250–900ms: opacity ramps to 0; the meeting underneath (already
           painted in the destination palette — cool for Ghost, warm for
           V/F) becomes visible. For Ghost POV the cool palette emerging
           through the fade is the felt chromatic-divergence event; for
           V/F it's a quieter dissolve onto warm paper.
       Total ~900ms — shorter than the desktop turn (1200ms) because there's
       no rotation to honor; the mobile layout collapses to a stack and the
       transition is purely a tonal handoff. */
    const tl = gsap.timeline({
      onComplete: () => {
        if (!hasFiredRef.current) {
          hasFiredRef.current = true;
          onComplete();
        }
      },
    });
    tl.to(snapshotRef.current, {
      filter: 'saturate(0.2)',
      duration: 0.25,
      ease: 'power2.in',
    }, 0);
    tl.to(snapshotRef.current, {
      opacity: 0,
      duration: 0.65,
      ease: 'power2.inOut',
    }, 0.25);

    return () => { tl.kill(); };
  }, [onComplete]);

  return (
    <div className="susdle-meeting-transition susdle-meeting-transition--mobile" aria-hidden>
      <div className="susdle-meeting-transition__snapshot" ref={snapshotRef}>
        {/* On mobile the spread is a single column with recto reordered
            ahead of verso (per Spread.css). We render the same content
            shape so the snapshot reads identical to the lobby the user
            just left. */}
        <Spread pov="village" verso={lobbyVerso} recto={lobbyRecto} />
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Lobby content snapshot — re-renders the lobby's verso + recto from the     */
/* captured LobbyView. Mirrors Lobby.tsx's CompanySection / RoundSection      */
/* inline rather than reaching into Lobby.tsx for un-exported helpers; the    */
/* duplication is tight (~30 lines) and isolates this cinematic from the      */
/* lobby's internal structure.                                                 */
/* -------------------------------------------------------------------------- */

function LobbyVersoSnapshot({ view }: { view: LobbyView }): JSX.Element {
  const { t } = useTranslation();
  const seats = useMemo<Seat[]>(() => {
    const playerSeats: Seat[] = view.players.map((player) => ({
      kind: 'player',
      player,
    }));
    const emptyCount = Math.max(0, MIN_PLAYERS - view.players.length);
    const emptySeats: Seat[] = Array.from({ length: emptyCount }, (_, i) => ({
      kind: 'empty',
      index: i,
    }));
    return [...playerSeats, ...emptySeats];
  }, [view.players]);

  const isHost = view.meId === view.hostId;
  const canStart = view.players.length >= MIN_PLAYERS;
  const hostName = view.players.find((p) => p.id === view.hostId)?.name ?? '';

  return (
    <>
      <div className="susdle-spread__group">
        {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- Safari/VoiceOver strip list semantics from <ul> when list-style:none is applied (see Lobby.css .susdle-company-grid); explicit role="list" restores them. */}
        <ul
          className="susdle-company-grid"
          role="list"
          aria-label={t('lobby.sections.company')}
        >
          <AnimatePresence initial={false}>
            {seats.map((seat) => {
              if (seat.kind === 'empty') {
                return (
                  <li key={`empty-${seat.index}`}>
                    <EmptyChair index={seat.index} />
                  </li>
                );
              }
              const { player } = seat;
              return (
                <li key={player.id}>
                  {/* Page-turn snapshot content — the float never plays
                      inside a cinematic (cold-arrival conventions). */}
                  <AvatarFrame player={player} isHost={player.id === view.hostId} suppressFloat />
                </li>
              );
            })}
          </AnimatePresence>
        </ul>
      </div>
      <div className="susdle-spread__group">
        <HostControls
          isHost={isHost}
          foolEnabled={view.settings.foolEnabled}
          timerPreset={view.settings.timerPreset}
          onFoolChange={noopBool}
          onTimerChange={noopTimer}
        />
      </div>
      <SignatureBand
        isHost={isHost}
        hostName={hostName}
        canStart={canStart}
        onStart={noop}
      />
    </>
  );
}

function LobbyRectoSnapshot({ view }: { view: LobbyView }): JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      <Wordmark
        preamble={t('lobby.titleStack.preamble')}
        title={t('app.title')}
      />
      <VillageIllustration size="recto" />
      <PrintedCast
        playerCount={view.players.length}
        foolEnabled={view.settings.foolEnabled}
        minPlayers={MIN_PLAYERS}
      />
    </>
  );
}

type Seat =
  | { kind: 'player'; player: LobbyPlayer }
  | { kind: 'empty'; index: number };

/* The snapshot's interactive controls are never touchable — the overlay
   sits above the live meeting and the snapshot itself fades out partway
   through. These no-ops satisfy the components' prop contracts without
   wiring a callback that can never fire meaningfully. */
const noop = (): void => undefined;
const noopBool = (_v: boolean): void => undefined;
const noopTimer = (_p: TimerPreset): void => undefined;
